<script setup lang="ts" generic="TLayer extends EntityRenderLayer">
import { nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';

import CircleDotSolid from '@/components/svg/CircleDotSolid.vue';
import type { EntityRenderLayer } from '@/typings/drawing/EntityRenderLayer';
import type { Polygon } from '@/typings/drawing/Polygon';

// Tooltip state
const tooltipVisible = ref(false);
const tooltipPosition = ref({ x: 0, y: 0 });
const tooltipLayer = ref<TLayer | null>(null);

defineOptions({
  inheritAttrs: false
});

const props = defineProps<{
  layers: TLayer[];
  imageUrl: string;
  width?: number;
  height?: number;
}>();

const emit = defineEmits<{
  'point-click': [point: Polygon, layer: TLayer];
  'canvas-loaded': [];
}>();

const svgElement = useTemplateRef<SVGSVGElement>('svgElement');
const planDimensions = ref({
  width: props.width || 0,
  height: props.height || 0
});
const resizeObserver = ref<ResizeObserver | undefined>();

const updatePlanDimensions = async () => {
  if (!props.width || !props.height) {
    try {
      const dimensions = await getImageDimensions(props.imageUrl);
      planDimensions.value = dimensions;
    } catch (error) {
      console.error('Failed to load image dimensions:', error);
    }
  }
}

onMounted(async () => {
  await updatePlanDimensions();

  nextTick(() => {
    emit('canvas-loaded');

    if (svgElement.value) {
      resizeObserver.value = new ResizeObserver((entries) => {
        handleResize(entries);
      });

      resizeObserver.value.observe(svgElement.value);
    }
  });
});

onUnmounted(() => {
  if (resizeObserver.value) {
    resizeObserver.value.disconnect();
  }
});

watch(() => props.imageUrl, async () =>{
  await updatePlanDimensions()
}) 

// formatPoints function no longer needed for points, but kept for compatibility
function formatPoints(points: { x: number, y: number }[]): string {
  return points.map(point => `${point.x},${point.y}`).join(' ');
}

function getImageDimensions(url: string): Promise<{ width: number, height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({
      width: img.naturalWidth,
      height: img.naturalHeight
    });
    img.onerror = reject;
    img.src = url;
  });
}

function handlePointClick(point: Polygon, layer: TLayer) {
  // Just emit the event, parent component handles selection state
  emit('point-click', point, layer);
}

function handlePointMouseenter(event: MouseEvent, layer: TLayer, point: Polygon) {
  tooltipLayer.value = layer;
  tooltipVisible.value = true;
  updateTooltipPosition(point);
}

function handlePointMouseleave() {
  tooltipVisible.value = false;
  tooltipLayer.value = null;
}

function updateTooltipPosition(point: Polygon) {
  if (!point.points.length || !svgElement.value) return;
  
  const svgRect = svgElement.value.getBoundingClientRect();
  const containerRect = svgElement.value.parentElement?.getBoundingClientRect();
  
  if (!containerRect) return;
  
  // Facteur d'échelle entre l'SVG et ses dimensions affichées
  const scaleX = svgRect.width / planDimensions.value.width;
  const scaleY = svgRect.height / planDimensions.value.height;
  
  // Position du point dans l'espace du conteneur - centré sur le point
  const pointX = point.points[0].x * scaleX;
  const pointY = point.points[0].y * scaleY;
  
  const offset = 50;
  
  let finalX = pointX;
  let finalY: number;
  
  // Essayer de placer au-dessus du point (centré)
  const tooltipAboveY = pointY - offset;
  
  if (tooltipAboveY >= 0) {
    // Il y a assez de place au-dessus
    finalY = tooltipAboveY;
  } else {
    // Pas assez de place au-dessus, placer en-dessous du point
    finalY = pointY + offset;
  }
  
  // S'assurer que le tooltip reste dans les limites horizontales
  // Le CSS utilise transform: translate(-50%, 0), donc on garde la position centrale
  const tooltipWidth = 120; // Largeur estimée du tooltip
  const halfTooltipWidth = tooltipWidth / 2;
  
  if (finalX - halfTooltipWidth < 0) {
    finalX = halfTooltipWidth;
  } else if (finalX + halfTooltipWidth > containerRect.width) {
    finalX = containerRect.width - halfTooltipWidth;
  }
  
  tooltipPosition.value = {
    x: finalX,
    y: finalY
  };
}

function handleResize(entries?: ResizeObserverEntry[]) {
  // Keep reference for future viewport calculations if needed
  console.debug('Canvas resized:', entries?.[0]?.contentRect);
}
</script>

<template>
  <div class="renderCanvas-container">
    <div class="renderCanvas"
         v-bind="$attrs">
      <svg ref="svgElement"
           :width="planDimensions.width"
           :height="planDimensions.height"
           :viewBox="`0 0 ${planDimensions.width} ${planDimensions.height}`"
           preserveAspectRatio="xMidYMid meet"
           class="renderCanvas-svg">
        <!-- Image d'arrière-plan -->
        <image :href="imageUrl"
               x="0"
               y="0"
               :width="planDimensions.width"
               :height="planDimensions.height"
               class="renderCanvas-backgroundImage" />

        <!-- Points par couche -->
        <g v-for="layer in layers"
           :key="layer.id"
           class="renderCanvas-layer">
          <template v-for="point in layer.polygons"
                    :key="`${layer.id}-${point.id}`">
            <slot name="point"
                  :point="point"
                  :layer="layer"
                  :handle-click="() => handlePointClick(point, layer)"
                  :onMouseenter="(event: MouseEvent) => handlePointMouseenter(event, layer, point)"
                  :onMouseleave="handlePointMouseleave">
              <!-- Rendu de point par défaut -->
              <g v-if="point.points.length > 0" class="renderCanvas-pointItem">
                <foreignObject
                  :x="Math.round(point.points[0].x - 16)"
                  :y="Math.round(point.points[0].y - 16)"
                  width="32"
                  height="32"
                  :class="[
                    'renderCanvas-polygonShape',
                    `renderCanvas-polygonShape_${layer.id}`
                  ]"
                  tabindex="0"
                  @click="handlePointClick(point, layer)"
                  @mouseenter="(event: MouseEvent) => handlePointMouseenter(event, layer, point)"
                  @mouseleave="handlePointMouseleave"
                >
                  <div class="renderCanvas-pointIcon">
                    <CircleDotSolid />
                  </div>
                </foreignObject>
              </g>
            </slot>
          </template>
        </g>
      </svg>
      
      <!-- Tooltip -->
      <div v-if="tooltipVisible && tooltipLayer"
           class="renderCanvas-htmlTooltip"
           :style="{
             left: tooltipPosition.x + 'px',
             top: tooltipPosition.y + 'px'
           }">
        <slot name="tooltip" :layer="tooltipLayer">
          <!-- Default tooltip content -->
          <div class="renderCanvas-defaultTooltip">
            Layer {{ tooltipLayer.id }}
          </div>
        </slot>
      </div>
    </div>
  </div>
</template>

<style>
.renderCanvas {
  position: relative;
  width: 100%;
  height: auto;
  border: var(--contentSizeBorderWidth) solid var(--contentColorBorder);
  
  border-radius: var(--radius-md);
  overflow: clip;
}

.renderCanvas-container {
  container: renderCanvas / inline-size;
}

.renderCanvas-svg {
  display: block;
  width: 100%;
  height: auto;
  max-width: 100%;
}

.renderCanvas-polygonShape {
  --polygonColor: var(--baseColor, oklch(.7 0 0));
  --polygonFillColor: oklch(from var(--polygonColor) l c h / 0.7);
  --polygonStrokeColor: oklch(from var(--polygonColor) calc(l * 1) c h / 1);
  transition: 
    scale 0.2s ease,
    stroke-width 0.2s ease,
    filter 0.2s ease;
  transform-box: fill-box;
  transform-origin: center;
  cursor: pointer;
  fill: var(--polygonFillColor);
  stroke: var(--polygonStrokeColor);
  stroke-width: 1;
}

.renderCanvas-polygonShape {
  &:is(:focus-visible, :hover),
  .renderCanvas-pointItem:is([data-hovered="true"], [data-unit-highlighted="true"]) & {
    opacity: 1;
    filter: brightness(1.2);
    transform: scale(1);
  }

  &:focus {
    outline: none;
  }

  .renderCanvas-pointItem:is([data-selected="true"]) &:not(:is(:focus-visible, :hover)) {
    opacity: 1;
    color: var(--pointActiveColor);
    filter: brightness(1.3);
    transform: scale(1);
  }
}

.renderCanvas-pointItem {
  cursor: pointer;
}

.renderCanvas-pointIcon{
  width:100%;
  height:100%;
  display:block;
  background-color:#000;
  border-radius:99px;
}

.renderCanvas-pointIcon svg {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--polygonColor);
  fill: var(--polygonFillColor);
  stroke: var(--polygonStrokeColor);
  transform-box: fill-box;
  transform-origin: center;
  stroke-width: 1;
}

/* Tooltip styles */
.renderCanvas-htmlTooltip {
  position: absolute;
  z-index: 1000;
  padding: 12px 20px;
  min-width: 120px;
  max-width: 280px;
  border-radius: 6px;
  background-color: var(--gray-950, #000);
  color: var(--white, #fff);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  opacity: 1;
  pointer-events: none;
  visibility: visible;
  transition: opacity 0.2s ease;
  font-size: 14px;
  line-height: 1.4;
  font-weight: 500;
  white-space: nowrap;
  transform: translate(-50%, -20%);
}
</style>
